import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Megaphone, RefreshCw, ChevronLeft, Plus, Upload, Play, Search, Users,
  CheckCircle2, Clock, MessageSquare, Phone, RotateCcw, ExternalLink, CornerDownLeft,
  CheckCheck, Eye, CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchCampaignOverview,
  listBulkCampaigns,
  createCampaign,
  scheduleCampaign,
  cancelCampaignSchedule,
  executeCampaign,
  processCampaignBatch,
  resendFailedCampaign,
  getCampaign,
  getCampaignAnalytics,
  getCampaignPreview,
  listAllCampaignRecipients,
  listTemplates,
  listTemplatePreviews,
  getTemplatePreview,
  approvedTemplates,
  isTemplateApproved,
  templateStatus,
  templateStatusLabel,
  personalizePreview,
  formatApiError,
} from '../lib/campaignService';
import { previewAudience } from '../lib/leadService';
import CampaignCsvUpload, { recipientsToManualText } from './CampaignCsvUpload';
import SchedulePicker, { isSchedulable } from './SchedulePicker';

const STATUS_LABELS = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const DELIVERY_STATUS_COLORS = {
  pending: '#6D668D',
  queued: '#8D57DE',
  submitted: '#5D27CA',
  sent: '#1F0798',
  delivered: '#22B07D',
  read: '#1a9a6c',
  failed: '#D25F86',
};

function formatDate(value) {
  if (!value) return '—';
  const raw = String(value).trim();
  const d = new Date(raw.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(raw) ? raw : `${raw.includes('T') ? raw : raw.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
}

function formatPct(v) {
  return `${Number(v || 0).toFixed(1)}%`;
}

function templateIdOf(t) {
  return t?.id || t?.templateId || '';
}

function templateNameOf(t) {
  return t?.elementName || t?.element_name || t?.name || templateIdOf(t);
}

const EMPTY_FORM = {
  campaign_name: '',
  template_id: '',
  template_name: '',
  audience: 'filtered',
  categories: [],
  zones: [],
  sources: [],
  lead_statuses: [],
  manual_phones: '',
  start_immediately: false,
  // 'draft' = save and send by hand, 'schedule' = send automatically later
  send_mode: 'draft',
  schedule_days: 1,
  schedule_time: '10:00',
};

/** Parse manual recipients.
 *  Supported lines:
 *    919876543210
 *    Rahul, 9876543210
 *    Rahul: 919876543210
 *    Rahul | 9876543210
 */
function parseManualPhones(text) {
  const lines = String(text || '')
    .split(/[\n\r]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const seen = new Set();
  const recipients = [];

  const pushPhone = (rawPhone, rawName) => {
    let digits = String(rawPhone || '').replace(/\D/g, '');
    if (digits.length < 10) return;
    if (digits.length === 10) digits = `91${digits}`;
    if (seen.has(digits)) return;
    seen.add(digits);
    const name = String(rawName || '').trim();
    recipients.push({
      phone_number: digits,
      name: name || undefined,
    });
  };

  for (const line of lines) {
    // Name separator forms first
    const named = line.match(/^(.+?)\s*[,|:]\s*([+\d][\d\s\-()]{8,})$/);
    if (named) {
      pushPhone(named[2], named[1]);
      continue;
    }
    const pipe = line.match(/^(.+?)\s*\|\s*([+\d][\d\s\-()]{8,})$/);
    if (pipe) {
      pushPhone(pipe[2], pipe[1]);
      continue;
    }
    // Plain phones on one line (comma/space separated)
    const parts = line.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    for (const part of parts) {
      if (!/\d{10,}/.test(part.replace(/\D/g, ''))) continue;
      pushPhone(part, '');
    }
  }
  return recipients;
}

function manualAudiencePreview(text) {
  const recipients = parseManualPhones(text).map((r, i) => ({
    id: `manual-${i}-${r.phone_number}`,
    name: r.name || '…',
    phone: r.phone_number,
    category: 'Manual',
  }));
  return {
    total_recipients: recipients.length,
    recipients,
    sample: recipients.slice(0, 10),
  };
}

function GupshupStatusBadge({ status }) {
  const s = String(status || '').toUpperCase();
  const approved = s === 'APPROVED' || s === 'ACTIVE';
  const pending = s === 'PENDING' || s === 'SUBMITTED';
  const rejected = s === 'REJECTED' || s === 'FAILED';
  let cls = 'gupshup-status-badge';
  if (approved) cls += ' gupshup-status-approved';
  else if (pending) cls += ' gupshup-status-pending';
  else if (rejected) cls += ' gupshup-status-rejected';
  return (
    <span className={cls}>
      {approved && <CheckCircle2 size={14} />}
      {pending && <Clock size={14} />}
      {templateStatusLabel(status)}
    </span>
  );
}

function TemplatePreviewCard({ preview, sampleName = 'Rahul', title = 'WhatsApp message preview' }) {
  if (!preview) return null;
  const body = personalizePreview(preview.body_text, sampleName);
  const buttons = preview.buttons?.length
    ? preview.buttons.filter((b) => b?.text)
    : (preview.button_text ? [{ text: preview.button_text, url: preview.button_url }] : []);
  return (
    <div className="campaign-preview-card">
      <div className="campaign-preview-header">
        <h3><MessageSquare size={18} /> {title}</h3>
        <GupshupStatusBadge status={preview.status} />
      </div>
      <p className="campaign-preview-meta">
        Template: <strong>{preview.element_name}</strong>
        {preview.template_id && (
          <> · ID <code className="campaign-template-id">{preview.template_id.slice(0, 8)}…</code></>
        )}
      </p>
      <div className="campaign-preview-body">
        {preview.image_url && (
          <img
            className="campaign-preview-image"
            src={preview.image_url}
            alt="Template header"
          />
        )}
        <div className="campaign-preview-text">
          <p className="campaign-preview-sample-label">Example for lead &quot;{sampleName}&quot;:</p>
          {preview.header && <p className="campaign-preview-header-text">{preview.header}</p>}
          <p className="campaign-preview-message">{body}</p>
          {preview.footer && <p className="campaign-preview-footer">{preview.footer}</p>}
          {buttons.length > 0 && (
            <div className="campaign-preview-buttons">
              {buttons.map((b, i) => (
                <span key={`${b.text}-${i}`} className="campaign-preview-button" title={b.url || b.phone_number || undefined}>
                  {b.type === 'URL' && <ExternalLink size={13} />}
                  {b.type === 'PHONE_NUMBER' && <Phone size={13} />}
                  {b.type === 'QUICK_REPLY' && <CornerDownLeft size={13} />}
                  {b.text}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {!isTemplateApproved({ status: preview.status }) && (
        <p className="campaign-preview-warning">
          This template is not approved yet. You can save a draft, but sending is blocked until Gupshup approves it.
        </p>
      )}
    </div>
  );
}

function CampaignFlowSteps({ step }) {
  const steps = [
    { n: 1, label: 'Gupshup-approved template' },
    { n: 2, label: 'Choose audience' },
    { n: 3, label: 'Save draft' },
    { n: 4, label: 'Trigger send' },
  ];
  return (
    <div className="campaign-flow-steps">
      {steps.map((s) => (
        <span key={s.n} className={`campaign-flow-step${step >= s.n ? ' active' : ''}`}>
          <span className="campaign-flow-num">{s.n}</span>
          {s.label}
        </span>
      ))}
    </div>
  );
}

const DELIVERY_BAR_ORDER = ['read', 'delivered', 'sent', 'failed', 'pending'];

function DeliveryStatusBar({ statusCounts, total }) {
  if (!total) return null;
  const counts = statusCounts || {};
  const sent = (counts.submitted || 0) + (counts.sent || 0) + (counts.queued || 0);
  const byKey = { read: counts.read || 0, delivered: counts.delivered || 0, sent, failed: counts.failed || 0, pending: counts.pending || 0 };
  // A read message was delivered first, so the delivered total includes reads.
  const totalDelivered = byKey.delivered + byKey.read;
  const pct = (n) => `${Math.round((n / total) * 100)}%`;
  return (
    <div className="campaign-delivery-bar-wrap" onClick={(e) => e.stopPropagation()}>
      <div className="campaign-card-totals">
        <span className="campaign-card-total">
          <CheckCheck size={16} style={{ color: DELIVERY_STATUS_COLORS.delivered }} />
          <strong>{totalDelivered.toLocaleString('en-IN')}</strong>
          <span>of {total.toLocaleString('en-IN')} delivered ({pct(totalDelivered)})</span>
        </span>
        <span className="campaign-card-total">
          <Eye size={16} style={{ color: DELIVERY_STATUS_COLORS.read }} />
          <strong>{byKey.read.toLocaleString('en-IN')}</strong>
          <span>read ({pct(byKey.read)})</span>
        </span>
      </div>
      <div className="campaign-delivery-bar">
        {DELIVERY_BAR_ORDER.map((key) => {
          const count = byKey[key];
          if (!count) return null;
          return (
            <span
              key={key}
              className="campaign-delivery-bar-segment"
              style={{ width: `${(count / total) * 100}%`, background: DELIVERY_STATUS_COLORS[key] || '#94a3b8' }}
              title={`${key}: ${count}`}
            />
          );
        })}
      </div>
      <div className="campaign-delivery-pills">
        {DELIVERY_BAR_ORDER.map((key) => (
          <span key={key} className="campaign-delivery-pill">
            <span className="campaign-delivery-dot" style={{ background: DELIVERY_STATUS_COLORS[key] || '#94a3b8' }} />
            {key === 'delivered' ? 'delivered, unread' : key} {byKey[key]}
          </span>
        ))}
      </div>
    </div>
  );
}

function MultiSelect({ label, options, selected, onChange }) {
  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };
  if (!options?.length) {
    return (
      <div className="campaign-filter-group">
        <span className="campaign-filter-label">{label}</span>
        <p className="campaign-filter-empty">Import leads to populate filters</p>
      </div>
    );
  }
  return (
    <div className="campaign-filter-group">
      <span className="campaign-filter-label">{label}</span>
      <div className="campaign-filter-chips">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`campaign-filter-chip${selected.includes(opt) ? ' active' : ''}`}
            onClick={() => toggle(opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CampaignManagerTab({ apiUrl, refreshMs = 30000, onOpenLeadImport }) {
  const [view, setView] = useState('list');
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [allTemplates, setAllTemplates] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  // template_id -> full preview, for the "Approved templates" view.
  const [approvedPreviews, setApprovedPreviews] = useState({});
  const [approvedPreviewsLoading, setApprovedPreviewsLoading] = useState(false);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // null = every matched lead is included; a Set = only these lead ids are included.
  const [selectedLeadIds, setSelectedLeadIds] = useState(null);
  const [saving, setSaving] = useState(false);
  // Reschedule editor on the detail page: null = closed, else {days, time, busy}.
  const [schedEdit, setSchedEdit] = useState(null);
  const [selected, setSelected] = useState(null);
  const [campaignPreview, setCampaignPreview] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientTotal, setRecipientTotal] = useState(0);
  const [recipientStatusFilter, setRecipientStatusFilter] = useState(null);
  const [listStatusFilter, setListStatusFilter] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [resendSummary, setResendSummary] = useState(null);
  // Live progress while the send-batch poll loop is running for the open campaign.
  const [sendProgress, setSendProgress] = useState(null);
  // Which button started the running send ('trigger' | 'resend'), and how many
  // numbers were pending when it started — live "done" = start - current pending.
  const [sendMode, setSendMode] = useState(null);
  const sendStartPendingRef = useRef(0);
  // True while the recipient table is still loading for the open campaign.
  const [detailLoading, setDetailLoading] = useState(false);
  // Guards against a slow response for a previously opened campaign landing late.
  const openedCampaignRef = useRef(null);

  const approvedList = approvedTemplates(allTemplates);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadOverview = useCallback(async () => {
    try {
      const data = await fetchCampaignOverview(apiUrl);
      setOverview(data);
    } catch (err) {
      console.error(err);
    }
  }, [apiUrl]);

  const loadCampaigns = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await listBulkCampaigns(apiUrl, {
        limit: 100,
        search: debouncedSearch || undefined,
        status: listStatusFilter || undefined,
      });
      setCampaigns(data?.items ?? []);
      setTotal(data?.total ?? 0);
    } catch (err) {
      console.error(err);
      if (!silent) toast.error('Failed to load campaigns');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [apiUrl, debouncedSearch, listStatusFilter]);

  const loadTemplates = useCallback(async () => {
    try {
      const raw = await listTemplates(apiUrl);
      setAllTemplates(raw);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load WhatsApp templates from Gupshup');
    }
  }, [apiUrl]);

  const loadApprovedPreviews = useCallback(async () => {
    setApprovedPreviewsLoading(true);
    try {
      const list = await listTemplatePreviews(apiUrl, { approvedOnly: true });
      setApprovedPreviews(Object.fromEntries(list.map((p) => [p.template_id, p])));
    } catch (err) {
      console.error(err);
      toast.error('Could not load template messages from Gupshup');
    } finally {
      setApprovedPreviewsLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (view === 'approved-templates') loadApprovedPreviews();
  }, [view, loadApprovedPreviews]);

  const loadTemplatePreview = useCallback(async (templateId) => {
    if (!templateId) {
      setTemplatePreview(null);
      return;
    }
    setTemplatePreviewLoading(true);
    try {
      const preview = await getTemplatePreview(apiUrl, templateId);
      setTemplatePreview(preview);
    } catch (err) {
      console.error(err);
      setTemplatePreview(null);
      toast.error('Could not load template message from Gupshup');
    } finally {
      setTemplatePreviewLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (view !== 'list') return undefined;
    loadOverview();
    loadCampaigns();
    loadTemplates();
    const id = setInterval(() => {
      if (!document.hidden) {
        loadOverview();
        loadCampaigns({ silent: true });
      }
    }, refreshMs);
    return () => clearInterval(id);
  }, [view, loadOverview, loadCampaigns, loadTemplates, refreshMs]);

  useEffect(() => {
    if (view === 'create') {
      loadTemplates();
      loadOverview();
    }
  }, [view, loadTemplates, loadOverview]);

  useEffect(() => {
    if (view === 'create' && form.template_id) {
      loadTemplatePreview(form.template_id);
    }
  }, [view, form.template_id, loadTemplatePreview]);

  // Filters change faster than the API can respond; without a sequence guard
  // a slow, now-stale request can resolve after a newer one and overwrite it.
  const previewRequestIdRef = useRef(0);

  const runAudiencePreview = async () => {
    const requestId = ++previewRequestIdRef.current;
    const isStale = () => requestId !== previewRequestIdRef.current;
    if (form.audience === 'manual') {
      const parsed = parseManualPhones(form.manual_phones);
      if (parsed.length === 0) {
        setAudiencePreview({ total_recipients: 0, recipients: [], sample: [] });
        setPreviewLoading(false);
        return;
      }
      // Show typed names immediately (don't wait on API / don't fall back to "there").
      const localPreview = {
        total_recipients: parsed.length,
        recipients: parsed.map((r, i) => ({
          id: `manual-${i}-${r.phone_number}`,
          name: r.name || null,
          phone: r.phone_number,
          category: 'Manual',
        })),
        sample: parsed.slice(0, 10).map((r, i) => ({
          id: `manual-${i}-${r.phone_number}`,
          name: r.name || null,
          phone: r.phone_number,
          category: 'Manual',
        })),
      };
      setAudiencePreview(localPreview);
      setPreviewLoading(true);
      try {
        const data = await previewAudience(apiUrl, {
          mode: 'manual',
          phones: parsed.map((r) => r.phone_number),
          names: parsed.map((r) => r.name || ''),
        });
        if (isStale()) return;
        const typedNameByPhone = new Map(
          parsed.map((r) => [r.phone_number, (r.name || '').trim()])
        );
        const mergeName = (row) => {
          const phone = String(row?.phone || '').replace(/\D/g, '');
          const typed = typedNameByPhone.get(phone) || '';
          const apiName = String(row?.name || '').trim();
          if (typed) return typed;
          if (apiName && apiName.toLowerCase() !== 'there') return apiName;
          return typed || apiName || 'there';
        };
        const recipients = (data?.recipients || localPreview.recipients).map((row) => ({
          ...row,
          name: mergeName(row),
        }));
        setAudiencePreview({
          total_recipients: data?.total_recipients ?? recipients.length,
          recipients,
          sample: recipients.slice(0, 10),
          breakdown: data?.breakdown,
        });
      } catch (err) {
        console.error(err);
        // Keep local typed-name preview
      } finally {
        if (!isStale()) setPreviewLoading(false);
      }
      return;
    }
    setPreviewLoading(true);
    try {
      const mode = form.audience === 'all' ? 'all' : 'filtered';
      const data = await previewAudience(apiUrl, {
        mode,
        categories: form.categories,
        zones: form.zones,
        sources: form.sources,
        lead_statuses: form.lead_statuses,
      });
      if (isStale()) return;
      setAudiencePreview(data);
    } catch (err) {
      if (!isStale()) toast.error(err.response?.data?.detail || 'Audience preview failed');
    } finally {
      if (!isStale()) setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (view !== 'create') return undefined;
    const timer = setTimeout(runAudiencePreview, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, form.audience, form.categories, form.zones, form.sources, form.lead_statuses, form.manual_phones]);

  // The matched-leads set changes whenever the audience/filters change, so any
  // per-lead selection made against the previous set is no longer valid.
  useEffect(() => {
    setSelectedLeadIds(null);
  }, [form.audience, form.categories, form.zones, form.sources, form.lead_statuses]);

  const previewRecipients = audiencePreview?.recipients || audiencePreview?.sample || [];
  const allSelected = selectedLeadIds === null;
  const effectiveTotal = form.audience === 'manual'
    ? (audiencePreview?.total_recipients ?? 0)
    : (allSelected ? (audiencePreview?.total_recipients ?? 0) : selectedLeadIds.size);

  const toggleLead = (id) => {
    setSelectedLeadIds((prev) => {
      const next = prev === null ? new Set(previewRecipients.map((r) => r.id)) : new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedLeadIds(allSelected ? new Set() : null);
  };

  // Numbers read from an uploaded CSV/Excel are added to the manual-numbers box, so the
  // normal preview / create / send flow handles them like typed numbers.
  const handleCsvApply = (csvRecipients) => {
    const text = recipientsToManualText(csvRecipients);
    setForm((f) => ({
      ...f,
      manual_phones: [String(f.manual_phones || '').trim(), text].filter(Boolean).join('\n'),
    }));
    toast.success(`${csvRecipients.length} number${csvRecipients.length === 1 ? '' : 's'} added from file`);
  };

  const handleSaveSchedule = async () => {
    if (!selected || !schedEdit) return;
    if (!isSchedulable(schedEdit.days, schedEdit.time)) {
      toast.error('Pick a send time in the future');
      return;
    }
    setSchedEdit((s) => ({ ...s, busy: true }));
    try {
      const updated = await scheduleCampaign(apiUrl, selected.id, {
        days_from_now: Number(schedEdit.days),
        time: schedEdit.time,
      });
      setSelected((prev) => ({ ...prev, ...updated }));
      setSchedEdit(null);
      toast.success('Campaign scheduled');
      await loadOverview();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not schedule the campaign'));
      setSchedEdit((s) => (s ? { ...s, busy: false } : s));
    }
  };

  const handleCancelSchedule = async () => {
    if (!selected) return;
    try {
      const updated = await cancelCampaignSchedule(apiUrl, selected.id);
      setSelected((prev) => ({ ...prev, ...updated }));
      setSchedEdit(null);
      toast.success('Schedule cancelled — campaign is back to draft');
      await loadOverview();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not cancel the schedule'));
    }
  };

  const handleCreate = async () => {
    if (!form.campaign_name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    if (!form.template_id) {
      toast.error('Select a template');
      return;
    }
    if (!templatePreview || !isTemplateApproved({ status: templatePreview.status })) {
      toast.error('Only Gupshup-approved templates can be used for campaigns');
      return;
    }

    const manualRecipients = form.audience === 'manual' ? parseManualPhones(form.manual_phones) : [];
    if (form.audience === 'manual' && manualRecipients.length === 0) {
      toast.error('Add at least one valid mobile number');
      return;
    }
    if (form.audience !== 'manual' && !effectiveTotal) {
      toast.error('No recipients selected for this audience');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        campaign_name: form.campaign_name.trim(),
        template_name: form.template_name,
        template_id: form.template_id,
        template_params: ['{{name}}'],
        audience: form.audience,
        audience_filters: form.audience === 'manual'
          ? {}
          : {
              categories: form.categories,
              zones: form.zones,
              sources: form.sources,
              lead_statuses: form.lead_statuses,
              lead_ids: allSelected ? [] : Array.from(selectedLeadIds),
            },
        recipients: form.audience === 'manual'
          ? manualRecipients.map((r) => ({
              phone_number: r.phone_number,
              name: r.name || undefined,
            }))
          : [],
        start_immediately: false,
        ...(form.send_mode === 'schedule'
          ? { schedule: { days_from_now: Number(form.schedule_days), time: form.schedule_time } }
          : {}),
      };
      const created = await createCampaign(apiUrl, payload);
      toast.success(
        form.send_mode === 'schedule'
          ? 'Campaign scheduled — it will send automatically at the chosen time'
          : 'Campaign saved as draft — review message, then trigger send'
      );
      setForm(EMPTY_FORM);
      setTemplatePreview(null);
      setAudiencePreview(null);
      setSelectedLeadIds(null);
      await openDetail(created);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  const loadRecipients = useCallback(async (campaignId, statusFilter = null) => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    const data = await listAllCampaignRecipients(apiUrl, campaignId, params);
    setRecipients(data?.items ?? []);
    setRecipientTotal(data?.total ?? 0);
  }, [apiUrl]);

  const openDetail = async (campaign) => {
    setSelected(campaign);
    setView('detail');
    setDetailLoading(true);
    setCampaignPreview(null);
    setAnalytics(null);
    setRecipients([]);
    setRecipientTotal(0);
    setRecipientStatusFilter(null);
    // Each section renders as soon as its own request lands, instead of the whole
    // page waiting on the slowest one (recipients on large campaigns).
    const isCurrent = () => openedCampaignRef.current === campaign.id;
    openedCampaignRef.current = campaign.id;

    const analyticsReq = getCampaignAnalytics(apiUrl, campaign.id)
      .then((a) => { if (isCurrent()) setAnalytics(a); })
      .catch((err) => console.error(err));

    const previewReq = getCampaignPreview(apiUrl, campaign.id)
      .catch(() => (campaign.template_id ? getTemplatePreview(apiUrl, campaign.template_id) : null))
      .then((p) => { if (isCurrent() && p) setCampaignPreview(p); })
      .catch(() => { /* optional */ });

    const recipientsReq = listAllCampaignRecipients(apiUrl, campaign.id)
      .then((r) => {
        if (!isCurrent()) return;
        setRecipients(r?.items ?? []);
        setRecipientTotal(r?.total ?? 0);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load campaign recipients');
      })
      .finally(() => { if (isCurrent()) setDetailLoading(false); });

    await Promise.allSettled([analyticsReq, previewReq, recipientsReq]);
  };

  const applyRecipientFilter = async (nextFilter) => {
    if (!selected?.id) return;
    const filter = recipientStatusFilter === nextFilter ? null : nextFilter;
    setRecipientStatusFilter(filter);
    try {
      await loadRecipients(selected.id, filter);
    } catch (err) {
      console.error(err);
      toast.error('Failed to filter recipients');
    }
  };

  /** Refreshes the campaign summary, analytics, and recipient table without the
   * full-page loading flash `openDetail` does — used between/after send batches. */
  const refreshCampaignDetail = useCallback(async (campaignId) => {
    try {
      const [c, a, r] = await Promise.allSettled([
        getCampaign(apiUrl, campaignId),
        getCampaignAnalytics(apiUrl, campaignId),
        listAllCampaignRecipients(apiUrl, campaignId, recipientStatusFilter ? { status: recipientStatusFilter } : {}),
      ]);
      if (c.status === 'fulfilled') setSelected(c.value);
      if (a.status === 'fulfilled') setAnalytics(a.value);
      if (r.status === 'fulfilled') {
        setRecipients(r.value?.items ?? []);
        setRecipientTotal(r.value?.total ?? 0);
      }
    } catch (err) {
      console.error(err);
    }
  }, [apiUrl, recipientStatusFilter]);

  /** Starts (or resumes) sending a campaign in batches until nothing's left pending,
   * reporting progress via onProgress after each batch. Shared by the detail-page
   * Trigger/Resume button and the list-view quick-resume button. */
  const runSendLoop = async (campaignId, onProgress, startWithExecute = true) => {
    let raw = startWithExecute
      ? await executeCampaign(apiUrl, campaignId)
      : await processCampaignBatch(apiUrl, campaignId);
    let progress = raw?.batch || raw || {};
    let totalSuccess = Number(progress.success || 0);
    let totalFailed = Number(progress.failed || 0);
    onProgress?.({ success: totalSuccess, failed: totalFailed, remaining: progress.remaining ?? 0 });

    while (
      Number(progress.remaining || 0) > 0
      && progress.campaign_status !== 'cancelled'
    ) {
      await new Promise((resolve) => setTimeout(resolve, progress.locked ? 2000 : 400));
      progress = (await processCampaignBatch(apiUrl, campaignId)) || {};
      if (progress.locked) {
        onProgress?.({ success: totalSuccess, failed: totalFailed, remaining: progress.remaining ?? 0 });
        continue;
      }
      totalSuccess += Number(progress.success || 0);
      totalFailed += Number(progress.failed || 0);
      onProgress?.({ success: totalSuccess, failed: totalFailed, remaining: progress.remaining ?? 0 });
    }
    return { totalSuccess, totalFailed, remaining: Number(progress.remaining || 0) };
  };

  // Each send request keeps going for minutes before it replies, so the loop's own
  // counts sit at 0 in the meantime. Poll the stored counts to show real progress.
  useEffect(() => {
    if (!triggering || !selected?.id) return undefined;
    const campaignId = selected.id;
    const id = setInterval(() => {
      if (document.hidden) return;
      getCampaignAnalytics(apiUrl, campaignId)
        .then((a) => { if (openedCampaignRef.current === campaignId) setAnalytics(a); })
        .catch(() => { /* next tick retries */ });
    }, 5000);
    return () => clearInterval(id);
  }, [triggering, selected?.id, apiUrl]);

  const handleTrigger = async () => {
    if (!selected?.id) return;
    const approved = campaignPreview && isTemplateApproved({ status: campaignPreview.status });
    if (!approved) {
      toast.error('Template must be approved by Gupshup before sending');
      return;
    }
    const campaignId = selected.id;
    const isFirstSend = selected.status === 'draft';
    if (isFirstSend && !window.confirm(`Send "${selected.template_name}" to ${selected.total_recipients} mobile numbers?`)) {
      return;
    }
    sendStartPendingRef.current = analytics?.pending || selected.total_recipients || 0;
    setSendMode('trigger');
    setTriggering(true);
    setSendProgress({ success: 0, failed: 0, remaining: null });
    try {
      const { totalSuccess, totalFailed, remaining } = await runSendLoop(campaignId, setSendProgress);
      toast.success(
        remaining > 0
          ? 'Sending paused — click Resume send to continue'
          : `Campaign finished — ${totalSuccess} sent, ${totalFailed} failed`
      );
    } catch (err) {
      toast.error(
        `${err.response?.data?.detail || 'Sending paused after an error'} — click Resume send to continue`
      );
    } finally {
      setTriggering(false);
      setSendMode(null);
      setSendProgress(null);
      await refreshCampaignDetail(campaignId);
    }
  };

  const handleResendFailed = async () => {
    if (!selected?.id) return;
    const failedCount = analytics?.failed || 0;
    if (failedCount <= 0) {
      toast.error('No failed numbers on this campaign');
      return;
    }
    const approved = campaignPreview && isTemplateApproved({ status: campaignPreview.status });
    if (!approved) {
      toast.error('Template must be approved by Gupshup before resending');
      return;
    }
    if (!window.confirm(
      `Resend "${selected.template_name}" to ${failedCount} failed number${failedCount === 1 ? '' : 's'} from this campaign? Delivered/read contacts will not be sent again.`
    )) {
      return;
    }
    const campaignId = selected.id;
    setRecipientStatusFilter(null);
    setResendSummary(null);
    // Resend moves failed rows back to pending, and the loop then sends everything
    // pending — including numbers never sent the first time.
    sendStartPendingRef.current = (analytics?.pending || 0) + failedCount;
    setSendMode('resend');
    setTriggering(true);
    setSendProgress({ success: 0, failed: 0, remaining: null });
    try {
      const queued = await resendFailedCampaign(apiUrl, campaignId);
      const targeted = queued?.targeted || queued?.reset || failedCount;
      const phones = queued?.resent_to || [];
      const { totalSuccess, totalFailed, remaining } = await runSendLoop(
        campaignId,
        setSendProgress,
        false,
      );
      const summary = {
        targeted,
        sent: totalSuccess,
        stillFailed: totalFailed,
        phones,
      };
      setResendSummary(summary);
      toast.success(
        remaining > 0
          ? `Resend paused — ${totalSuccess} of ${targeted} submitted. Click Resume send to continue.`
          : `Resent to ${targeted} numbers — ${totalSuccess} submitted, ${totalFailed} still failed`
      );
    } catch (err) {
      toast.error(formatApiError(err, 'Resend failed'));
    } finally {
      setTriggering(false);
      setSendMode(null);
      setSendProgress(null);
      await refreshCampaignDetail(campaignId);
    }
  };


  const meta = overview || {};
  const filterOptions = {
    categories: meta.categories || [],
    zones: meta.zones || [],
    sources: meta.sources || [],
    lead_statuses: meta.lead_statuses || [],
  };

  const templateApproved = templatePreview && isTemplateApproved({ status: templatePreview.status });
  const detailTemplateApproved = campaignPreview && isTemplateApproved({ status: campaignPreview.status });

  const startCampaignWithTemplate = (tpl) => {
    const id = templateIdOf(tpl);
    setForm((f) => ({
      ...f,
      template_id: id,
      template_name: templateNameOf(tpl),
      campaign_name: f.campaign_name || templateNameOf(tpl),
    }));
    setView('create');
  };

  if (view === 'approved-templates') {
    return (
      <div className="campaigns-section">
        <div className="campaigns-header">
          <button type="button" className="campaign-back-btn" onClick={() => setView('list')}>
            <ChevronLeft size={18} /> Back
          </button>
          <h2><CheckCircle2 size={22} /> Approved templates ({approvedList.length})</h2>
          <button type="button" className="refresh-btn" onClick={() => { loadTemplates(); loadApprovedPreviews(); }}>
            <RefreshCw size={18} />
          </button>
        </div>
        <p className="campaign-manager-hint">
          These templates are approved on Gupshup/Meta. Each card shows the exact message — image, text and
          buttons — that recipients will get. Click <strong>Use in campaign</strong> to start a campaign with it.
        </p>
        {approvedList.length === 0 ? (
          <div className="empty-state">
            <CheckCircle2 size={48} />
            <p>No approved templates yet</p>
            <span>Create a template in the Templates tab — it will appear here after Meta approval.</span>
          </div>
        ) : (
          <div className="campaign-approved-templates-list">
            {approvedList.map((t) => {
              const id = templateIdOf(t);
              const preview = approvedPreviews[id];
              return (
                <div key={id} className="campaign-approved-template-card">
                  <div className="campaign-approved-template-head">
                    <div>
                      <h3>{templateNameOf(t)}</h3>
                      <div className="campaign-approved-template-meta">
                        <GupshupStatusBadge status={templateStatus(t)} />
                        <span>{t.category || 'UTILITY'}</span>
                        <span>{t.languageCode || t.language_code || 'en'}</span>
                        <code className="campaign-template-id">{id.slice(0, 8)}…</code>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="campaign-primary-btn"
                      onClick={() => startCampaignWithTemplate(t)}
                    >
                      Use in campaign
                    </button>
                  </div>
                  {preview ? (
                    <TemplatePreviewCard preview={preview} title="Message recipients will get" />
                  ) : (
                    <p className="campaign-preview-loading">
                      {approvedPreviewsLoading ? 'Loading message from Gupshup…' : 'Message preview unavailable'}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (view === 'create') {
    return (
      <div className="campaigns-section">
        <div className="campaigns-header">
          <button type="button" className="campaign-back-btn" onClick={() => { setView('list'); setTemplatePreview(null); }}>
            <ChevronLeft size={18} /> Back
          </button>
          <h2><Plus size={22} /> Create Campaign</h2>
        </div>

        <CampaignFlowSteps step={form.template_id ? (audiencePreview?.total_recipients ? 3 : 2) : 1} />

        <div className="campaign-form campaign-form-wide">
          <label>
            Campaign name
            <input
              value={form.campaign_name}
              onChange={(e) => setForm((f) => ({ ...f, campaign_name: e.target.value }))}
              placeholder="e.g. Elevated Living — Architects Q3"
            />
          </label>

          <label>
            WhatsApp template (Gupshup)
            <select
              value={form.template_id}
              onChange={(e) => {
                const tpl = allTemplates.find((t) => templateIdOf(t) === e.target.value);
                setForm((f) => ({
                  ...f,
                  template_id: e.target.value,
                  template_name: tpl ? templateNameOf(tpl) : '',
                }));
              }}
            >
              <option value="">Select template…</option>
              {approvedList.length > 0 && (
                <optgroup label="Approved — ready to send">
                  {approvedList.map((t) => {
                    const id = templateIdOf(t);
                    return (
                      <option key={id} value={id}>
                        {templateNameOf(t)} (Approved)
                      </option>
                    );
                  })}
                </optgroup>
              )}
              {allTemplates.filter((t) => !isTemplateApproved(t)).length > 0 && (
                <optgroup label="Pending / not approved (preview only)">
                  {allTemplates.filter((t) => !isTemplateApproved(t)).map((t) => {
                    const id = templateIdOf(t);
                    return (
                      <option key={id} value={id}>
                        {templateNameOf(t)} ({templateStatusLabel(templateStatus(t))})
                      </option>
                    );
                  })}
                </optgroup>
              )}
            </select>
          </label>

          {templatePreviewLoading && <p className="campaign-preview-loading">Loading message from Gupshup…</p>}
          {!templatePreviewLoading && templatePreview && (
            <TemplatePreviewCard
              preview={templatePreview}
              sampleName={
                audiencePreview?.recipients?.[0]?.name
                || audiencePreview?.sample?.[0]?.name
                || 'Rahul'
              }
              title="Message approved on Gupshup"
            />
          )}

          <label>
            Audience
            <select
              value={form.audience}
              onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))}
            >
              <option value="all">All leads</option>
              <option value="filtered">Filtered leads</option>
              <option value="manual">Manual mobile numbers</option>
            </select>
          </label>

          {form.audience === 'manual' && (
            <>
            <label className="campaign-manual-phones-field">
              Mobile numbers
              <textarea
                value={form.manual_phones}
                onChange={(e) => setForm((f) => ({ ...f, manual_phones: e.target.value }))}
                rows={6}
                placeholder={'One per line. Optional name:\nRahul, 9876543210\n919876543210\nPriya: 919812345678'}
              />
              <small>
                Use <strong>Name, number</strong> to set the greeting name. If you paste only a number,
                we look up the lead name automatically (falls back to &quot;there&quot;).
              </small>
            </label>
            <CampaignCsvUpload apiUrl={apiUrl} onApply={handleCsvApply} />
            </>
          )}

          {form.audience === 'filtered' && (
            <>
              <MultiSelect
                label="Category"
                options={filterOptions.categories}
                selected={form.categories}
                onChange={(v) => setForm((f) => ({ ...f, categories: v }))}
              />
              <MultiSelect
                label="Zone"
                options={filterOptions.zones}
                selected={form.zones}
                onChange={(v) => setForm((f) => ({ ...f, zones: v }))}
              />
              <MultiSelect
                label="Source"
                options={filterOptions.sources}
                selected={form.sources}
                onChange={(v) => setForm((f) => ({ ...f, sources: v }))}
              />
              <MultiSelect
                label="Lead status"
                options={filterOptions.lead_statuses}
                selected={form.lead_statuses}
                onChange={(v) => setForm((f) => ({ ...f, lead_statuses: v }))}
              />
            </>
          )}

          <div className="campaign-audience-preview">
            <h3><Phone size={16} /> Recipients (mobile numbers)</h3>
            {previewLoading ? (
              <p>Calculating…</p>
            ) : (
              <>
                <p className="campaign-audience-count">
                  <Users size={16} />
                  <strong>{effectiveTotal}</strong> mobile number{effectiveTotal === 1 ? '' : 's'} will receive this template
                  {form.audience !== 'manual' && audiencePreview?.total_recipients > 0 && !allSelected && (
                    <span> (of {audiencePreview.total_recipients} matched)</span>
                  )}
                </p>
                {previewRecipients.length > 0 && (
                  <div className="campaign-audience-table-wrap">
                    {form.audience !== 'manual' && (
                      <div className="campaign-audience-select-actions">
                        <button type="button" className="campaign-link-btn" onClick={toggleSelectAll}>
                          {allSelected ? 'Deselect all' : 'Select all'}
                        </button>
                        <span>Uncheck a lead to leave it out of this campaign.</span>
                      </div>
                    )}
                    <table className="campaign-messages-table campaign-sample-table">
                      <thead>
                        <tr>
                          {form.audience !== 'manual' && (
                            <th>
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={toggleSelectAll}
                                title="Select all"
                              />
                            </th>
                          )}
                          <th>#</th>
                          <th>Name</th>
                          <th>Mobile</th>
                          <th>Category</th>
                          <th>Zone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewRecipients.map((r, i) => (
                          <tr key={r.id || `${r.phone}-${i}`}>
                            {form.audience !== 'manual' && (
                              <td>
                                <input
                                  type="checkbox"
                                  checked={allSelected || selectedLeadIds.has(r.id)}
                                  onChange={() => toggleLead(r.id)}
                                />
                              </td>
                            )}
                            <td>{i + 1}</td>
                            <td>{r.name || '—'}</td>
                            <td className="campaign-phone-cell">{r.phone}</td>
                            <td>{r.category || '—'}</td>
                            <td>{r.zone || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>

          <label>
            When to send
            <select
              value={form.send_mode}
              onChange={(e) => setForm((f) => ({ ...f, send_mode: e.target.value }))}
            >
              <option value="draft">Save as draft — I will trigger it myself</option>
              <option value="schedule">Schedule — send automatically later</option>
            </select>
          </label>

          {form.send_mode === 'schedule' && (
            <SchedulePicker
              days={form.schedule_days}
              time={form.schedule_time}
              onChange={({ days, time }) => setForm((f) => ({ ...f, schedule_days: days, schedule_time: time }))}
            />
          )}

          <button
            type="button"
            className="campaign-primary-btn"
            onClick={handleCreate}
            disabled={
              saving || previewLoading || !templateApproved
              || (form.audience !== 'manual' && !effectiveTotal)
              || (form.send_mode === 'schedule' && !isSchedulable(form.schedule_days, form.schedule_time))
            }
            title={!templateApproved ? 'Select an approved Gupshup template first' : undefined}
          >
            {saving ? 'Creating…' : (form.send_mode === 'schedule' ? 'Schedule campaign' : 'Save as draft')}
          </button>
        </div>
      </div>
    );
  }

  if (view === 'detail' && selected) {
    const isFirstSend = selected.status === 'draft';
    // A scheduled campaign starts itself at its time; sending it by hand is blocked server-side too.
    const canTrigger = selected.status !== 'scheduled' && (
      ['draft', 'queued', 'failed', 'running'].includes(selected.status)
      || (analytics?.pending || 0) > 0
    ) && detailTemplateApproved;
    // Live figures from the polled counts; the loop's own totals only move when a
    // (multi-minute) send request returns, so take whichever is further along.
    const livePending = analytics?.pending ?? null;
    const liveDone = livePending == null ? 0 : Math.max(0, sendStartPendingRef.current - livePending);
    const loopDone = sendProgress ? sendProgress.success + sendProgress.failed : 0;
    const progressLabel = `Sending… ${Math.max(liveDone, loopDone).toLocaleString('en-IN')} done`
      + (livePending != null ? `, ${livePending.toLocaleString('en-IN')} left` : '');
    const triggerLabel = triggering && sendMode === 'trigger'
      ? progressLabel
      : (isFirstSend ? 'Trigger send' : 'Resume send');
    return (
      <div className="campaigns-section">
        <div className="campaigns-header">
          <button type="button" className="campaign-back-btn" onClick={() => { setView('list'); setSelected(null); }}>
            <ChevronLeft size={18} /> Back
          </button>
          <div>
            <h2>{selected.campaign_name}</h2>
            <p className="campaign-detail-sub">
              Template: <strong>{selected.template_name}</strong>
              {' · '}{selected.total_recipients} mobile numbers
            </p>
          </div>
          <div className="section-header-actions">
            {canTrigger && (
              <button
                type="button"
                className="campaign-primary-btn"
                onClick={handleTrigger}
                disabled={triggering}
              >
                <Play size={16} />
                {triggerLabel}
              </button>
            )}
            {(analytics?.failed || 0) > 0 && detailTemplateApproved && (
              <button
                type="button"
                className="campaign-secondary-btn"
                onClick={handleResendFailed}
                disabled={triggering}
              >
                <RotateCcw size={16} />
                {triggering && sendMode === 'resend' ? progressLabel : `Resend to ${analytics.failed} failed`}
              </button>
            )}
            {!detailTemplateApproved && campaignPreview && (
              <span className="campaign-trigger-blocked">Awaiting Gupshup approval</span>
            )}
            <button type="button" className="refresh-btn" onClick={() => openDetail(selected)}>
              <RefreshCw size={18} />
            </button>
          </div>
        </div>

        <CampaignFlowSteps step={selected.status === 'completed' ? 4 : selected.status === 'running' ? 4 : 3} />

        <div className="campaign-detail-status-row">
          <span className={`campaign-status-badge status-${selected.status}`}>
            Campaign: {STATUS_LABELS[selected.status] || selected.status}
          </span>
          {campaignPreview && <GupshupStatusBadge status={campaignPreview.status} />}
          <span>Created {formatDate(selected.created_at)}</span>
          {selected.triggered_at && <span>Triggered {formatDate(selected.triggered_at)}</span>}
        </div>

        {(selected.status === 'scheduled' || selected.status === 'draft') && (
          <div className="campaign-schedule-panel">
            {selected.status === 'scheduled' && selected.scheduled_at && (
              <p className="campaign-schedule-line">
                <CalendarClock size={16} />
                Scheduled for <strong>{formatDate(selected.scheduled_at)} IST</strong> — it sends
                automatically, within about 5 minutes of that time.
              </p>
            )}
            {schedEdit ? (
              <>
                <SchedulePicker
                  days={schedEdit.days}
                  time={schedEdit.time}
                  disabled={schedEdit.busy}
                  onChange={({ days, time }) => setSchedEdit((s) => ({ ...s, days, time }))}
                />
                <div className="campaign-import-actions">
                  <button
                    type="button"
                    className="campaign-primary-btn"
                    onClick={handleSaveSchedule}
                    disabled={schedEdit.busy || !isSchedulable(schedEdit.days, schedEdit.time)}
                  >
                    {schedEdit.busy ? 'Saving…' : 'Save schedule'}
                  </button>
                  <button
                    type="button"
                    className="campaign-secondary-btn"
                    onClick={() => setSchedEdit(null)}
                    disabled={schedEdit.busy}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <div className="campaign-import-actions">
                <button
                  type="button"
                  className="campaign-secondary-btn"
                  onClick={() => setSchedEdit({ days: selected.schedule_days ?? 1, time: selected.schedule_time || '10:00', busy: false })}
                >
                  <CalendarClock size={16} />
                  {selected.status === 'scheduled' ? 'Reschedule' : 'Schedule send'}
                </button>
                {selected.status === 'scheduled' && (
                  <button type="button" className="campaign-secondary-btn" onClick={handleCancelSchedule}>
                    Cancel schedule
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {resendSummary && (
          <div className="campaign-resend-summary">
            <strong>Last resend:</strong>{' '}
            {resendSummary.targeted} number{resendSummary.targeted === 1 ? '' : 's'} queued
            {' — '}{resendSummary.sent} submitted, {resendSummary.stillFailed} still failed
            {resendSummary.phones?.length > 0 && (
              <ul className="campaign-resend-phone-list">
                {resendSummary.phones.slice(0, 50).map((p) => (
                  <li key={p.id || p.phone_number}>
                    {p.name || '—'} · {p.phone_number}
                  </li>
                ))}
                {resendSummary.phones.length > 50 && (
                  <li>…and {resendSummary.phones.length - 50} more</li>
                )}
              </ul>
            )}
          </div>
        )}

        {triggering && (
          <p className="campaign-manager-hint">
            {progressLabel}. Numbers go out one by one and this updates every few seconds —
            keep this tab open until it finishes (closing it pauses sending).
            {analytics && ` Delivered ${analytics.delivered.toLocaleString('en-IN')}, read ${analytics.read.toLocaleString('en-IN')}, failed ${analytics.failed.toLocaleString('en-IN')}.`}
          </p>
        )}

        {selected.status === 'running' && !triggering && (analytics?.pending ?? 0) > 0 && (
          <p className="campaign-manager-hint">
            {analytics.pending} recipient{analytics.pending === 1 ? '' : 's'} still haven't been sent to —
            click <strong>Resume send</strong> above to continue (sending happens in batches, so a large
            campaign may need this a few times).
          </p>
        )}

        <>
            {campaignPreview && (
              <TemplatePreviewCard
                preview={campaignPreview}
                sampleName={recipients[0]?.name || 'Rahul'}
                title="Message sent to each mobile number"
              />
            )}

            {analytics && (
              <div className="stats-grid campaign-stats-grid">
                <button
                  type="button"
                  className={`stat-card clickable${recipientStatusFilter == null ? ' active' : ''}`}
                  onClick={() => applyRecipientFilter(null)}
                >
                  <div className="stat-info">
                    <span className="stat-number">{analytics.total_recipients}</span>
                    <span className="stat-title">Mobile numbers</span>
                  </div>
                </button>
                <button
                  type="button"
                  className={`stat-card clickable${recipientStatusFilter === 'delivered,read' ? ' active' : ''}`}
                  onClick={() => applyRecipientFilter('delivered,read')}
                >
                  <div className="stat-info">
                    <span className="stat-number">{analytics.delivered}</span>
                    <span className="stat-title">Delivered</span>
                  </div>
                </button>
                <button
                  type="button"
                  className={`stat-card clickable${recipientStatusFilter === 'read' ? ' active' : ''}`}
                  onClick={() => applyRecipientFilter('read')}
                >
                  <div className="stat-info">
                    <span className="stat-number">{analytics.read}</span>
                    <span className="stat-title">Read</span>
                  </div>
                </button>
                <button
                  type="button"
                  className={`stat-card clickable${recipientStatusFilter === 'failed' ? ' active' : ''}`}
                  onClick={() => applyRecipientFilter('failed')}
                >
                  <div className="stat-info">
                    <span className="stat-number">{analytics.failed}</span>
                    <span className="stat-title">Failed</span>
                  </div>
                </button>
                {(analytics.retry_scheduled || 0) > 0 && (
                  <div className="stat-card" title="Failed with a temporary error; will be sent once more automatically">
                    <div className="stat-info">
                      <span className="stat-number">{analytics.retry_scheduled}</span>
                      <span className="stat-title">Auto-retry pending</span>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className={`stat-card clickable${recipientStatusFilter === 'delivered,read' ? ' active' : ''}`}
                  onClick={() => applyRecipientFilter('delivered,read')}
                >
                  <div className="stat-info">
                    <span className="stat-number">{formatPct(analytics.delivery_rate)}</span>
                    <span className="stat-title">Delivery rate</span>
                  </div>
                </button>
                <button
                  type="button"
                  className={`stat-card clickable${recipientStatusFilter === 'read' ? ' active' : ''}`}
                  onClick={() => applyRecipientFilter('read')}
                >
                  <div className="stat-info">
                    <span className="stat-number">{formatPct(analytics.read_rate)}</span>
                    <span className="stat-title">Read rate</span>
                  </div>
                </button>
              </div>
            )}

            <div className="campaign-messages-table-wrap">
              <h3>
                <Phone size={16} /> Delivery status by mobile number ({recipients.length}
                {recipientTotal > recipients.length ? ` of ${recipientTotal}` : ''}
                {recipientStatusFilter ? ` · filtered` : ''})
              </h3>
              <p className="campaign-manager-hint">
                Messages that fail with a temporary error are sent once more automatically, about 3 hours later
                (shown as <em>Auto-retry</em> below). Permanent failures, such as a number that is not on
                WhatsApp, are not retried.
              </p>
              {recipientStatusFilter === 'failed' && (analytics?.failed || 0) > 0 && (
                <p className="campaign-manager-hint">
                  These failed numbers belong only to this campaign. Use
                  {' '}<strong>Resend to {analytics.failed} failed</strong> above to send the same template again.
                </p>
              )}
              <table className="campaign-messages-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Mobile number</th>
                    <th>Template</th>
                    <th>WhatsApp status</th>
                    <th>Sent</th>
                    <th>Delivered</th>
                    <th>Read</th>
                    <th>Failure</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="campaign-empty-row">
                        {detailLoading ? 'Loading mobile numbers…' : 'No recipients yet'}
                      </td>
                    </tr>
                  ) : recipients.map((r, i) => (
                    <tr key={r.id}>
                      <td>{i + 1}</td>
                      <td>{r.name || '—'}</td>
                      <td className="campaign-phone-cell">{r.phone_number}</td>
                      <td className="campaign-template-cell">{selected.template_name}</td>
                      <td>
                        <span
                          className="campaign-msg-status"
                          style={{ background: DELIVERY_STATUS_COLORS[r.delivery_status] || '#94a3b8' }}
                        >
                          {r.delivery_status}
                        </span>
                      </td>
                      <td>{formatDate(r.sent_at)}</td>
                      <td>{formatDate(r.delivered_at)}</td>
                      <td>{formatDate(r.read_at)}</td>
                      <td className="failure-cell">
                        {r.failure_reason || '—'}
                        {r.failure_code && <small className="failure-code">code {r.failure_code}</small>}
                        {r.retry_at && (
                          <small className="retry-badge">Auto-retry {formatDate(r.retry_at)} IST</small>
                        )}
                        {r.retry_count > 0 && (
                          <small className="retry-badge done">
                            Retried once{r.previous_failure_reason ? ` — first attempt: ${r.previous_failure_reason}` : ''}
                          </small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        </>
      </div>
    );
  }

  return (
    <div className="campaigns-section">
      <div className="campaigns-header">
        <h2><Megaphone size={24} /> Campaign Manager</h2>
        <div className="campaigns-actions">
          <div className="campaign-search">
            <Search size={16} />
            <input
              type="search"
              placeholder="Search campaigns…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button type="button" className="campaign-primary-btn" onClick={() => setView('create')}>
            <Plus size={16} /> New campaign
          </button>
          <button type="button" className="refresh-btn" onClick={() => { loadOverview(); loadCampaigns(); loadTemplates(); }}>
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      <CampaignFlowSteps step={1} />

      <p className="campaign-manager-hint">
        Templates are submitted to Meta via Gupshup. Only <strong>Approved</strong> templates can be triggered.
        You will see the exact message text before sending.
      </p>

      <div className="stats-grid campaign-overview-grid">
        <button
          type="button"
          className={`stat-card clickable${listStatusFilter == null ? ' active' : ''}`}
          onClick={() => setListStatusFilter(null)}
        >
          <div className="stat-info">
            <span className="stat-number">{meta.total_leads ?? 0}</span>
            <span className="stat-title">Total leads</span>
          </div>
        </button>
        <button
          type="button"
          className={`stat-card clickable${view === 'approved-templates' ? ' active' : ''}`}
          onClick={() => {
            loadTemplates();
            setView('approved-templates');
          }}
        >
          <div className="stat-info">
            <span className="stat-number">{approvedList.length}</span>
            <span className="stat-title">Approved templates</span>
          </div>
        </button>
        <button
          type="button"
          className={`stat-card clickable${listStatusFilter == null ? ' active' : ''}`}
          onClick={() => setListStatusFilter(null)}
        >
          <div className="stat-info">
            <span className="stat-number">{meta.total_campaigns ?? 0}</span>
            <span className="stat-title">Campaigns</span>
          </div>
        </button>
        <button
          type="button"
          className={`stat-card clickable${listStatusFilter === 'draft' ? ' active' : ''}`}
          onClick={() => setListStatusFilter((s) => (s === 'draft' ? null : 'draft'))}
        >
          <div className="stat-info">
            <span className="stat-number">{meta.draft ?? 0}</span>
            <span className="stat-title">Drafts</span>
          </div>
        </button>
        <button
          type="button"
          className={`stat-card clickable${listStatusFilter === 'scheduled' ? ' active' : ''}`}
          onClick={() => setListStatusFilter((s) => (s === 'scheduled' ? null : 'scheduled'))}
        >
          <div className="stat-info">
            <span className="stat-number">{meta.scheduled ?? 0}</span>
            <span className="stat-title">Scheduled</span>
          </div>
        </button>
        <button
          type="button"
          className={`stat-card clickable${listStatusFilter === 'running' ? ' active' : ''}`}
          onClick={() => setListStatusFilter((s) => (s === 'running' ? null : 'running'))}
        >
          <div className="stat-info">
            <span className="stat-number">{meta.running ?? 0}</span>
            <span className="stat-title">Running</span>
          </div>
        </button>
        <button
          type="button"
          className={`stat-card clickable${listStatusFilter === 'completed' ? ' active' : ''}`}
          onClick={() => setListStatusFilter((s) => (s === 'completed' ? null : 'completed'))}
        >
          <div className="stat-info">
            <span className="stat-number">{meta.completed ?? 0}</span>
            <span className="stat-title">Completed</span>
          </div>
        </button>
      </div>

      <div className="campaign-import-panel">
        <h3>Leads</h3>
        <p>
          Import or update leads from a CSV or Excel file in the Import Leads tab. Repeated mobile numbers
          are merged automatically.
        </p>
        {onOpenLeadImport && (
          <div className="campaign-import-actions">
            <button type="button" className="campaign-secondary-btn" onClick={onOpenLeadImport}>
              <Upload size={16} /> Open Import Leads
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading">Loading campaigns…</div>
      ) : campaigns.length === 0 ? (
        <div className="empty-state">
          <Megaphone size={48} />
          <p>No campaigns yet</p>
          <span>Import leads, pick an approved Gupshup template, then create your first campaign.</span>
        </div>
      ) : (
        <div className="campaign-list">
          {campaigns.map((c) => (
            <div
              key={c.id}
              className="campaign-card campaign-card-expanded"
              onClick={() => openDetail(c)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && openDetail(c)}
            >
              <div className="campaign-card-top-row">
                <div className="campaign-card-main">
                  <h3>{c.campaign_name}</h3>
                  <p className="campaign-card-template">
                    <MessageSquare size={14} />
                    {c.template_name}
                  </p>
                  <p className="campaign-card-phones">
                    <Phone size={14} />
                    {c.total_recipients} mobile number{c.total_recipients === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="campaign-card-meta">
                  <span className={`campaign-status-badge status-${c.status}`}>
                    {STATUS_LABELS[c.status] || c.status}
                  </span>
                  {c.status === 'scheduled' && c.scheduled_at && (
                    <span className="campaign-card-scheduled">Sends {formatDate(c.scheduled_at)}</span>
                  )}
                  <span>{formatDate(c.created_at)}</span>
                </div>
              </div>
              <DeliveryStatusBar statusCounts={c.status_counts} total={c.total_recipients} />
            </div>
          ))}
          <p className="campaign-list-footer">{total} campaign(s) total</p>
        </div>
      )}
    </div>
  );
}
